// File: src/components/feeds/ActivityFeed/ActivityCard.tsx

import React, { useCallback, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { Avatar, Typography, Card, IconButton } from "../../../components";
import { formatDistanceToNow } from "date-fns";
import { useSwipeable } from "react-swipeable";
import { X } from "lucide-react";
import { CardWrapper, Content, HeaderLine, MetaLine, Ripple, XP } from "./ActivityCard.styles";

export interface ActivityItem {
  id: string;
  user: {
    name: string;
    avatarUrl?: string;
  };
  activity: string;
  metadata?: {
    xp?: number;
    badge?: string;
    info?: string;
  };
  timestamp: string | Date;
  dismissible?: boolean | undefined
}

interface ActivityCardProps {
  item: ActivityItem;
  onDismiss?: (id: string) => void;
  onClick?: (item: ActivityItem) => void;
}

const ClickableArea = styled.div`
  flex: 1;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

export const ActivityCard: React.FC<ActivityCardProps> = ({ item, onDismiss, onClick }) => {
    const rippleRef = useRef<HTMLSpanElement>(null);
    const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  
    const handleClick = useCallback(
        (e: React.MouseEvent) => {
          const ripple = rippleRef.current;
          if (ripple) {
            ripple.style.left = `${e.nativeEvent.offsetX - 50}px`;
            ripple.style.top = `${e.nativeEvent.offsetY - 50}px`;
            ripple.classList.remove("ripple");
            void ripple.offsetWidth;
            ripple.classList.add("ripple");
          }
    
          if (onClick) {
            onClick(item);
          }
        },
        [onClick, item]
      );
  
      const handleMouseDown = () => {
        longPressTimer.current = setTimeout(() => {
          console.log("🔍 Long press triggered for:", item.user.name);
        }, 800);
      };
  
    const handleMouseUp = () => {
      if (longPressTimer.current) clearTimeout(longPressTimer.current);
    };
  
    return (
      <CardWrapper
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onTouchStart={handleMouseDown}
        onTouchEnd={handleMouseUp}
      >
        <Ripple ref={rippleRef} className="ripple" />
  
        <Avatar size="sm" src={item.user.avatarUrl} name={item.user.name} />
        <ClickableArea onClick={handleClick}>
        <Content>
          <HeaderLine>
            <Typography variant="body" as="span">
              <strong>{item.user.name}</strong>
              <span style={{ fontWeight: 400 }}>{item.activity}</span>
            </Typography>
            {item.metadata?.xp && <XP>+{item.metadata.xp} XP</XP>}
          </HeaderLine>
  
          <MetaLine>
            {formatDistanceToNow(new Date(item.timestamp), { addSuffix: true })}
          </MetaLine>
        </Content>
        </ClickableArea>
        {item.dismissible && (
          <IconButton
            icon={<X size={18} />}
            ariaLabel="Dismiss"
            size="small"
            variant="ghost"
            onClick={(e) => {
                // e.stopPropagation(); // ✅ Prevent bubbling up to CardWrapper
                onDismiss?.(item.id);
              }}
            //   onMouseDown={(e: { stopPropagation: () => any; }) => e.stopPropagation()} // ✅ prevent ripple/card trigger
            //   onTouchStart={(e: { stopPropagation: () => any; }) => e.stopPropagation()} // ✅ also prevent for mobile
            
          />
        )}
      </CardWrapper>
    );
  };


  export const DismissibleActivityCard: React.FC<ActivityCardProps> = ({ item, onDismiss, onClick }) => {
    const handlers = useSwipeable({
      onSwipedLeft: () => item.dismissible && onDismiss && onDismiss(item.id),
      delta: 40,
      preventScrollOnSwipe: true,
      trackTouch: true,
    });
  
    return (
      <div {...handlers}>
        <ActivityCard item={item} onDismiss={onDismiss} onClick={onClick} />
      </div>
    );
  };